import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, Edit2, Trash2, Check, Tag, Palette } from "lucide-react";
import { toast } from "sonner";

const API = '';

// Color options for subtabs
const SUBTAB_COLORS = [
  { name: "Default", value: null, bg: "bg-[#141B2D]", text: "text-gray-400", activeBg: "bg-primary", activeText: "text-white" },
  { name: "Blue", value: "blue", bg: "bg-blue-500/20", text: "text-blue-400", activeBg: "bg-blue-500", activeText: "text-white" },
  { name: "Green", value: "green", bg: "bg-green-500/20", text: "text-green-400", activeBg: "bg-green-500", activeText: "text-white" },
  { name: "Yellow", value: "yellow", bg: "bg-yellow-500/20", text: "text-yellow-400", activeBg: "bg-yellow-500", activeText: "text-black" },
  { name: "Orange", value: "orange", bg: "bg-orange-500/20", text: "text-orange-400", activeBg: "bg-orange-500", activeText: "text-white" },
  { name: "Red", value: "red", bg: "bg-red-500/20", text: "text-red-400", activeBg: "bg-red-500", activeText: "text-white" },
  { name: "Purple", value: "purple", bg: "bg-purple-500/20", text: "text-purple-400", activeBg: "bg-purple-500", activeText: "text-white" },
  { name: "Pink", value: "pink", bg: "bg-pink-500/20", text: "text-pink-400", activeBg: "bg-pink-500", activeText: "text-white" },
  { name: "Cyan", value: "cyan", bg: "bg-cyan-500/20", text: "text-cyan-400", activeBg: "bg-cyan-500", activeText: "text-black" },
];

// Helper to get color classes for a subtab
const getSubtabColorClasses = (color, isSelected) => {
  const colorConfig = SUBTAB_COLORS.find(c => c.value === color) || SUBTAB_COLORS[0];
  if (isSelected) {
    return `${colorConfig.activeBg} ${colorConfig.activeText}`;
  }
  return `${colorConfig.bg} ${colorConfig.text} hover:bg-[#2D3B4F]`;
};

// ==================== SUBTAB MANAGER COMPONENT ====================
// Shared component for managing subtabs in Exercise, Workout, and Program libraries

export const SubtabManager = ({ 
  libraryType, // "exercises", "workouts", or "programs"
  selectedCategory, // Current selected category (speed, strength, etc.)
  selectedSubtab, // Currently selected subtab filter
  onSubtabSelect, // Callback when subtab is selected
}) => {
  const [subtabs, setSubtabs] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSubtab, setEditingSubtab] = useState(null);
  const [newSubtabName, setNewSubtabName] = useState("");
  const [newSubtabColor, setNewSubtabColor] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch subtabs for this library and category
  useEffect(() => {
    if (selectedCategory) {
      fetchSubtabs();
    } else {
      setSubtabs([]);
    }
  }, [libraryType, selectedCategory]);

  const fetchSubtabs = async () => {
    try {
      const params = new URLSearchParams({
        library_type: libraryType,
        category: selectedCategory
      });
      const response = await fetch(`${API}/api/coach/subtabs?${params}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSubtabs(data.subtabs || []);
      }
    } catch (error) {
      console.error("Failed to fetch subtabs:", error);
    }
  };

  const handleCreateSubtab = async () => {
    if (!newSubtabName.trim()) {
      toast.error("Please enter a subtab name");
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/coach/subtabs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: newSubtabName.trim(),
          category: selectedCategory,
          library_type: libraryType,
          color: newSubtabColor
        })
      });

      if (response.ok) {
        toast.success(`Subtab "${newSubtabName}" created!`);
        setNewSubtabName("");
        setNewSubtabColor(null);
        setShowCreateModal(false);
        fetchSubtabs();
      } else if (response.status === 409) {
        toast.error("A subtab with this name already exists");
      } else {
        toast.error("Failed to create subtab");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubtab = async (subtabId, updates) => {
    try {
      const response = await fetch(`${API}/api/coach/subtabs/${subtabId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        toast.success("Subtab updated!");
        setEditingSubtab(null);
        fetchSubtabs();
      } else {
        toast.error("Failed to update subtab");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const handleDeleteSubtab = async (subtab) => {
    if (!window.confirm(`Delete subtab "${subtab.name}"? Items won't be deleted, just untagged.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/coach/subtabs/${subtab.subtab_id}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Subtab deleted!");
        if (selectedSubtab === subtab.name) {
          onSubtabSelect(""); // Clear selection if deleted subtab was selected
        }
        fetchSubtabs();
      } else {
        toast.error("Failed to delete subtab");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  // Don't show if no category selected
  if (!selectedCategory) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      {/* All button (clears subtab filter) */}
      <button
        onClick={() => onSubtabSelect("")}
        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
          !selectedSubtab
            ? "bg-[#374B69] text-white"
            : "bg-[#141B2D] text-gray-400 hover:bg-[#2D3B4F]"
        }`}
        data-testid="subtab-all"
      >
        All
      </button>

      {/* Subtab pills */}
      {subtabs.map((subtab) => (
        <div key={subtab.subtab_id} className="relative group">
          {editingSubtab === subtab.subtab_id ? (
            <EditSubtabPopover
              subtab={subtab}
              onSave={(updates) => handleUpdateSubtab(subtab.subtab_id, updates)}
              onClose={() => setEditingSubtab(null)}
            />
          ) : (
            <button
              onClick={() => onSubtabSelect(subtab.name)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                getSubtabColorClasses(subtab.color, selectedSubtab === subtab.name)
              }`}
              data-testid={`subtab-${subtab.subtab_id}`}
            >
              {subtab.color && (
                <span className={`w-2 h-2 rounded-full ${
                  SUBTAB_COLORS.find(c => c.value === subtab.color)?.activeBg || 'bg-zinc-500'
                }`} />
              )}
              {!subtab.color && <Tag className="w-3 h-3" />}
              {subtab.name}
            </button>
          )}
          
          {/* Edit/Delete buttons on hover */}
          {editingSubtab !== subtab.subtab_id && (
            <div className="absolute -top-2 -right-2 hidden group-hover:flex items-center gap-0.5 bg-[#0B0F1A] rounded-full p-0.5 shadow-lg border border-[#1E293B]">
              <button
                onClick={(e) => { e.stopPropagation(); setEditingSubtab(subtab.subtab_id); }}
                className="p-1 hover:bg-[#2D3B4F] rounded-full text-gray-500 hover:text-white"
                title="Edit"
              >
                <Edit2 className="w-3 h-3" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleDeleteSubtab(subtab); }}
                className="p-1 hover:bg-red-500/20 rounded-full text-gray-500 hover:text-red-500"
                title="Delete"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      ))}

      {/* Add Subtab button */}
      <button
        onClick={() => setShowCreateModal(true)}
        className="px-3 py-1.5 rounded-full text-sm font-medium bg-[#1E293B]/30 text-gray-500 hover:bg-[#2D3B4F] hover:text-white transition-colors flex items-center gap-1 border border-dashed border-[#1E293B]"
        data-testid="add-subtab-btn"
      >
        <Plus className="w-3 h-3" />
        Add Subtab
      </button>

      {/* Create Subtab Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#141B2D] border border-[#1E293B] rounded-2xl w-full max-w-sm p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold">Create Subtab</h3>
                <button 
                  onClick={() => setShowCreateModal(false)}
                  className="p-1 hover:bg-[#1E293B] rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-sm text-gray-400 mb-4">
                Create a subtab to organize your {libraryType} within "{selectedCategory}"
              </p>

              <input
                type="text"
                placeholder="Subtab name (e.g., Upper Body, Week 1)"
                value={newSubtabName}
                onChange={(e) => setNewSubtabName(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleCreateSubtab()}
                className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white mb-4 focus:border-primary outline-none"
                autoFocus
                data-testid="subtab-name-input"
              />

              {/* Color Picker */}
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Color (optional)
                </label>
                <div className="flex flex-wrap gap-2">
                  {SUBTAB_COLORS.map((color) => (
                    <button
                      key={color.value || 'default'}
                      type="button"
                      onClick={() => setNewSubtabColor(color.value)}
                      className={`w-8 h-8 rounded-full border-2 transition-all flex items-center justify-center ${
                        color.value ? color.activeBg : 'bg-[#2D3B4F]'
                      } ${
                        newSubtabColor === color.value
                          ? 'border-white scale-110'
                          : 'border-transparent hover:border-zinc-500'
                      }`}
                      title={color.name}
                    >
                      {newSubtabColor === color.value && (
                        <Check className={`w-4 h-4 ${color.value === 'yellow' || color.value === 'cyan' ? 'text-black' : 'text-white'}`} />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-[#1E293B] text-white py-2.5 rounded-lg font-medium hover:bg-[#2D3B4F] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSubtab}
                  disabled={loading || !newSubtabName.trim()}
                  className="flex-1 bg-primary text-white py-2.5 rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="create-subtab-confirm"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Create
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== EDIT SUBTAB POPOVER ====================
const EditSubtabPopover = ({ subtab, onSave, onClose }) => {
  const [name, setName] = useState(subtab.name);
  const [color, setColor] = useState(subtab.color);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), color });
  };

  return (
    <div className="bg-[#0B0F1A] border border-[#1E293B] rounded-xl p-3 shadow-xl min-w-[200px]">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyPress={(e) => e.key === "Enter" && handleSave()}
        className="w-full bg-[#1E293B] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-white mb-3 focus:border-primary outline-none"
        autoFocus
      />
      
      <div className="flex flex-wrap gap-1.5 mb-3">
        {SUBTAB_COLORS.map((c) => (
          <button
            key={c.value || 'default'}
            type="button"
            onClick={() => setColor(c.value)}
            className={`w-6 h-6 rounded-full border-2 transition-all flex items-center justify-center ${
              c.value ? c.activeBg : 'bg-[#2D3B4F]'
            } ${
              color === c.value
                ? 'border-white'
                : 'border-transparent hover:border-zinc-500'
            }`}
            title={c.name}
          >
            {color === c.value && (
              <Check className={`w-3 h-3 ${c.value === 'yellow' || c.value === 'cyan' ? 'text-black' : 'text-white'}`} />
            )}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <button
          onClick={onClose}
          className="flex-1 bg-[#1E293B] text-white py-1.5 rounded-lg text-sm hover:bg-[#2D3B4F]"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="flex-1 bg-primary text-white py-1.5 rounded-lg text-sm hover:bg-blue-600"
        >
          Save
        </button>
      </div>
    </div>
  );
};

// ==================== SUBTAB SELECTOR FOR FORMS ====================
// Used in create/edit forms to assign items to subtabs

export const SubtabSelector = ({
  libraryType,
  category,
  selectedSubtabs = [],
  onChange,
}) => {
  const [subtabs, setSubtabs] = useState([]);
  const [newSubtab, setNewSubtab] = useState("");
  const [newSubtabColor, setNewSubtabColor] = useState(null);
  const [showInput, setShowInput] = useState(false);

  useEffect(() => {
    if (category) {
      fetchSubtabs();
    }
  }, [libraryType, category]);

  const fetchSubtabs = async () => {
    try {
      const params = new URLSearchParams({
        library_type: libraryType,
        category: category
      });
      const response = await fetch(`${API}/api/coach/subtabs?${params}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSubtabs(data.subtabs || []);
      }
    } catch (error) {
      console.error("Failed to fetch subtabs:", error);
    }
  };

  const toggleSubtab = (subtabName) => {
    const newSelection = selectedSubtabs.includes(subtabName)
      ? selectedSubtabs.filter(s => s !== subtabName)
      : [...selectedSubtabs, subtabName];
    onChange(newSelection);
  };

  const handleCreateAndSelect = async () => {
    if (!newSubtab.trim()) return;
    
    try {
      const response = await fetch(`${API}/api/coach/subtabs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: newSubtab.trim(),
          category: category,
          library_type: libraryType,
          color: newSubtabColor
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Subtab "${newSubtab}" created!`);
        setNewSubtab("");
        setNewSubtabColor(null);
        setShowInput(false);
        fetchSubtabs();
        // Auto-select the new subtab
        onChange([...selectedSubtabs, data.name]);
      } else if (response.status === 409) {
        toast.error("This subtab already exists");
      }
    } catch (error) {
      toast.error("Failed to create subtab");
    }
  };

  if (!category) return null;

  return (
    <div>
      <label className="block text-sm text-gray-400 mb-2">
        Subtabs (optional)
      </label>
      <div className="flex flex-wrap gap-2">
        {subtabs.map((subtab) => (
          <button
            key={subtab.subtab_id}
            type="button"
            onClick={() => toggleSubtab(subtab.name)}
            className={`px-3 py-1.5 rounded-full text-sm transition-colors flex items-center gap-1.5 ${
              selectedSubtabs.includes(subtab.name)
                ? getSubtabColorClasses(subtab.color, true)
                : getSubtabColorClasses(subtab.color, false)
            }`}
          >
            {subtab.color && (
              <span className={`w-2 h-2 rounded-full ${
                selectedSubtabs.includes(subtab.name)
                  ? 'bg-white/50'
                  : SUBTAB_COLORS.find(c => c.value === subtab.color)?.activeBg || 'bg-zinc-500'
              }`} />
            )}
            {!subtab.color && <Tag className="w-3 h-3" />}
            {subtab.name}
          </button>
        ))}
        
        {showInput ? (
          <div className="flex items-center gap-1">
            <input
              type="text"
              placeholder="New subtab..."
              value={newSubtab}
              onChange={(e) => setNewSubtab(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleCreateAndSelect()}
              className="bg-[#1E293B] border border-[#1E293B] rounded-full px-3 py-1.5 text-sm text-white w-32 focus:border-primary outline-none"
              autoFocus
            />
            {/* Mini color picker */}
            <div className="flex gap-0.5">
              {SUBTAB_COLORS.slice(0, 5).map((c) => (
                <button
                  key={c.value || 'default'}
                  type="button"
                  onClick={() => setNewSubtabColor(c.value)}
                  className={`w-5 h-5 rounded-full border ${
                    c.value ? c.activeBg : 'bg-[#2D3B4F]'
                  } ${
                    newSubtabColor === c.value ? 'border-white' : 'border-transparent'
                  }`}
                  title={c.name}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={handleCreateAndSelect}
              className="p-1.5 bg-primary rounded-full text-white hover:bg-blue-600"
            >
              <Check className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => { setShowInput(false); setNewSubtab(""); setNewSubtabColor(null); }}
              className="p-1.5 bg-[#2D3B4F] rounded-full text-white hover:bg-[#374B69]"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowInput(true)}
            className="px-3 py-1.5 rounded-full text-sm bg-[#1E293B]/30 text-gray-500 hover:bg-[#2D3B4F] hover:text-white transition-colors flex items-center gap-1 border border-dashed border-[#1E293B]"
          >
            <Plus className="w-3 h-3" />
            New
          </button>
        )}
      </div>
    </div>
  );
};

export default SubtabManager;
